// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import { useLookupIP } from "@outschool/iplookup-client";
import { useLocalStorageState } from "@outschool/local-storage";
import { useSession } from "@outschool/ui-auth";
import { isEmpty } from "lodash";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useContentfulBanners } from "./ContentfulBanners";
import { ParentCtaInterface } from "./types";

const isDevelopment =
  (process.env.OUTSCHOOL_ENV ?? "development") === "development";

/**
 * Queries Contentful (if enabled) for banners at a certain placement.  Can provide
 * a default set of banners when Contentful is not enabled or fails to load.
 */
export default function useBanners(
  placement: string,
  defaultBanners: (JSX.Element | ParentCtaInterface)[] = [],
  isFullWidth: boolean = false,
): ParentCtaInterface | undefined {
  /**
   * Adding ?previewBanner=<banner name> to a page where this hook is used
   * will make this hook override the default selection logic.
   *
   * Instead, it will
   *   - include unpublished banners in the Contentful query
   *   - override eligibility to display the matched banner
   * We can then add a preview link to Contentful that will show an editor
   * how their new banner will appear at staging.outschool.dev/path?previewBanner=MyNewBanner
   *
   * If you add previewBanner=<anything other than a banner name> you will get the
   * set of unpublished banners, but without a specific banner selected (and it will
   * use eligibility rules to select a banner from that set)
   */

  const { previewBanner: previewBannerName } = useLocation<{
    query: Record<string, any>;
    // @ts-ignore TS(2339): Property 'query' does not exist on type 'Location<... Remove this comment to see the full error message
  }>()?.query;
  const { banners: contentfulBanners, loading } = useContentfulBanners(
    placement,
    !!previewBannerName,
    isFullWidth,
  );
  const banners = loading
    ? []
    : contentfulBanners || wrapDefaultBanners(defaultBanners);

  const nextBanner = useNextBanner(loading, banners, placement);
  const previewBanner = banners.find(
    banner => banner.name === previewBannerName,
  );

  return previewBanner || nextBanner;
}

function useNextBanner(
  loading: boolean,
  banners: ParentCtaInterface[],
  placement: string,
) {
  const { currentUser, currentUserIsLoading } = useSession();
  const { ipInfo, ipInfoLoaded } = useLookupIP();

  const [lastBannerShown, setLastBannerShown] = useLocalStorageState(
    `LAST_HOME_PAGE_BANNER_SHOWN_${placement}`,
    "",
  );

  const [selectedParentCta, setSelectedParentCta] = React.useState<
    ParentCtaInterface | undefined
  >(undefined);

  useEffect(() => {
    if (loading || currentUserIsLoading) {
      return;
    }
    const eligibleCtas = banners.filter(cta =>
      cta.isEligible({ currentUser, ipInfo }),
    );
    if (
      !isEmpty(eligibleCtas) &&
      !selectedParentCta &&
      // ip lookup needs IPLOOKUP_API_KEY IPLOOKUP_API_BASE_URL env vars
      (ipInfoLoaded || isDevelopment)
    ) {
      let nextHomePageBannerToShow;
      if (lastBannerShown) {
        nextHomePageBannerToShow =
          eligibleCtas[
            (eligibleCtas
              .map(eligibleCta => eligibleCta.name)
              .indexOf(lastBannerShown) +
              1) %
              eligibleCtas.length
          ];
      } else {
        nextHomePageBannerToShow = eligibleCtas[0];
      }
      setSelectedParentCta(nextHomePageBannerToShow);
      setLastBannerShown(nextHomePageBannerToShow?.name);
    }
  }, [
    banners,
    loading,
    currentUser,
    currentUserIsLoading,
    ipInfoLoaded,
    lastBannerShown,
    selectedParentCta,
    setLastBannerShown,
    ipInfo,
  ]);

  return selectedParentCta;
}

function wrapDefaultBanners(
  banners: (JSX.Element | ParentCtaInterface)[],
): ParentCtaInterface[] {
  return banners.map(banner => {
    if (banner.hasOwnProperty("isEligible")) {
      return banner as ParentCtaInterface;
    } else {
      return {
        name: "default banner",
        isEligible: () => true,
        Component: () => banner as JSX.Element,
      };
    }
  });
}
